import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AppDispatch, RootState } from '../../store/configureStore';
import { Cell, PointOfSale } from '@m4a3/api-client';
import { getCells, setSelectedCell, getLandingZonesInCell } from '../../actions/orderActions';
import FilteredDropdown from '../../components/FilteredDropdown/FilteredDropdown';
import { getPointOfSales } from '../../actions/adminActions';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { createMerchantDeviceForCell, deleteMerchantDeviceById, updateMerchantDevice } from '../../actions/merchantDeviceActions';
import { MerchantDevice, MerchantDeviceCreationData } from '@m4a3/api-client/dist/models/merchant-device.interface';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { XCircleIcon } from '@heroicons/react/20/solid';
import { selectLandingZones } from 'selectors/ordersSelector';
import { useAppSelector } from 'hooks/redux';

interface ConfigureMerchantDeviceProps {
  cells: Cell[];
  selectedCellId: Cell['id']
  pointOfSales: PointOfSale[];
  isCreatingMerchantDevice: boolean;
  isUpdatingMerchantDevice: boolean;
  isDeletingMerchantDevice: boolean;
  merchantDeviceCreationError: Error;
  merchantDeviceUpdateError: Error;
  merchantDeviceDeletionError: Error;
  getCells: () => void;
  getPointofSales: (cellId: Cell['id']) => void;
  getLandingZonesInCell: (cellId: Cell['id']) => void;
  setSelectedCellId: (cellId: Cell['id']) => void;
  createMerchantDeviceForCell: (cellId: Cell['id'], merchantDevice: MerchantDeviceCreationData) => Promise<void>;
  updateMerchantDevice: (cellId: Cell['id'], merchantDeviceId: string, merchantDevice: MerchantDevice) => Promise<void>;
  deleteMerchantDeviceById: (cellId: Cell['id'], merchantDeviceId: string) => {};
}

const ConfigureMerchantDevice = ({
  cells,
  selectedCellId,
  pointOfSales,
  isCreatingMerchantDevice,
  isUpdatingMerchantDevice,
  isDeletingMerchantDevice,
  merchantDeviceCreationError,
  merchantDeviceUpdateError,
  merchantDeviceDeletionError,
  getCells,
  getPointofSales,
  getLandingZonesInCell,
  setSelectedCellId,
  createMerchantDeviceForCell,
  updateMerchantDevice,
  deleteMerchantDeviceById
}: Readonly<ConfigureMerchantDeviceProps>) => {
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const landingZones = useAppSelector(selectLandingZones)
  const location = useLocation();
  const merchantDevice = location?.state?.merchantDevice || null;
  const [username, setUsername] = useState<string>(merchantDevice?.username || "");
  const [description, setDescription] = useState<string>(merchantDevice?.description || "");
  const [pointOfSaleIds, setPointOfSaleIds] = useState<PointOfSale['id'][]>(merchantDevice?.merchantIds || []);
  const [androidDeviceId, setAndroidDeviceId] = useState<string>(merchantDevice?.androidDeviceId || "");
  const [additionalFilter, setAdditionalFilter] = useState<string>(merchantDevice?.additionalFilter || "");
  const [showAddFilterPanel, setShowAddFilterPanel] = useState<boolean>(false);
  const [storeSearch, setStoreSearch] = useState<string>('');
  const [selectedFilterStoreId, setSelectedFilterStoreId] = useState<number | null>(null);
  const [selectedFilterLzId, setSelectedFilterLzId] = useState<string | number | null>(null);
  const naviate = useNavigate();

  const filterPairs = React.useMemo(() => {
    const tokens = (additionalFilter || '')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    return tokens.map(token => {
      const [storeIdStr, lzId] = token.split(':');
      const storeId = Number(storeIdStr);
      const store: any = (pointOfSales || []).find((p: any) => p?.id === storeId);
      const lz: any = (landingZones || []).find((z: any) => z?.id === lzId);
      return { token, storeId, lzId, store, lz };
    });
  }, [additionalFilter, pointOfSales, landingZones]);

  const removeFilterToken = (tokenToRemove: string) => {
    const remaining = (additionalFilter || '')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean)
      .filter(t => t !== tokenToRemove)
      .join(',');
    setAdditionalFilter(remaining);
  };
  const [lzFilters, setLzFilters] = useState<Record<string, string>>({});
  const [lzOnlySelected, setLzOnlySelected] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!cells?.length) {
      getCells();
    }
  }, []);

  useEffect(() => {
    getPointofSales(selectedCellId);
    getLandingZonesInCell(selectedCellId);
  }, [selectedCellId]);


  useEffect(() => {
    setIsFormValid(checkIsFormValid())
  }, [username, pointOfSaleIds, androidDeviceId]);

  const createMerchantDevice = async (): Promise<void> => {
    if (!isFormValid) {
      return;
    }
    const merchantDevice: MerchantDeviceCreationData = {
      username,
      description,
      merchantIds: pointOfSaleIds,
      code: '000000',
      additionalFilter: additionalFilter
    } as any;
    
    // Add androidDeviceId if it exists
    if (androidDeviceId) {
      (merchantDevice as any).androidDeviceId = androidDeviceId;
    }
    
    await createMerchantDeviceForCell(selectedCellId, merchantDevice);
    naviate('../merchant-devices', {replace: true});
  };

  const handleUpdateMerchantDevice = async (): Promise<void> => {
    if (!isFormValid) {
      return;
    }

    merchantDevice.username = username;
    merchantDevice.description = description;
    merchantDevice.merchantIds = pointOfSaleIds;
    merchantDevice.androidDeviceId = androidDeviceId;
    merchantDevice.additionalFilter = additionalFilter;

    await updateMerchantDevice(selectedCellId, merchantDevice.id, merchantDevice);
    naviate('../merchant-devices', {replace: true});
  };

  const handleDeleteMerchantDevice = async (): Promise<void> => {
    await deleteMerchantDeviceById(selectedCellId, merchantDevice.id);
    naviate('../merchant-devices', {replace: true});
  };

  const clearForm = () => {
    setUsername('');
    setDescription('');
    setPointOfSaleIds([]);
    setAndroidDeviceId('');
    setAdditionalFilter('');
  };

  const checkIsFormValid = (): boolean => {
    return Boolean(username) && Boolean(selectedCellId) && pointOfSaleIds.length > 0;
  }

  const normaliseAndSetUsername = (eircode: string) => {
    const normalisedUsername = eircode.replace(/\s/g, '').toLowerCase();
    setUsername(normalisedUsername);
  }

  const getHeader = ():JSX.Element => {
    return <header className="p-6 bg-white">
      <div className="md:flex md:items-center md:justify-between">
        {merchantDevice ?
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Update Merchant Device
          </h2>
        </div> :
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            New Merchant Device
          </h2>
        </div>}
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <Link
            to="../merchant-devices"
            className="inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Back
          </Link>
          {merchantDevice ?
            <></> :
            <button
              type="button"
              onClick={clearForm}
              className="ml-3 inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Clear
            </button>
          }
          {merchantDevice ?
          <>
            <button
              type="button"
              disabled={!isFormValid || isUpdatingMerchantDevice}
              onClick={handleUpdateMerchantDevice}
              className={`ml-3 inline-flex items-center rounded-md ${isFormValid ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-200 cursor-not-allowed hover:bg-blue-200 text-slate-500'} px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
            >
              Update {isUpdatingMerchantDevice ? <div className='ml-2'> <LoadingSpinner size={20} /></div> : <></>}
            </button>
            <button
              type="button"
              disabled={!isFormValid || isDeletingMerchantDevice}
              onClick={handleDeleteMerchantDevice}
              className={`ml-3 inline-flex items-center rounded-md ${isFormValid ? 'bg-red-600 hover:bg-red-700' : 'bg-red-200 cursor-not-allowed hover:bg-red-200 text-slate-500'} px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600`}
            >
              Delete {isDeletingMerchantDevice ? <div className='ml-2'> <LoadingSpinner size={20} /></div> : <></>}
            </button>
          </>
          :
          <button
            type="button"
            disabled={!isFormValid || isCreatingMerchantDevice}
            onClick={createMerchantDevice}
            className={`ml-3 inline-flex items-center rounded-md ${isFormValid ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-200 cursor-not-allowed hover:bg-blue-200 text-slate-500'} px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
          >
            Create {isCreatingMerchantDevice ? <div className='ml-2'> <LoadingSpinner size={20} /></div> : <></>}
          </button>}
        </div>

      </div>
    </header>;
  };

  return (
    <div>
      {getHeader()}

      <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-6 p-6">
        <div className="sm:col-span-3">
          <div>
            <div className="mt-2">
              <FilteredDropdown
                label="Cell *"
                value={selectedCellId || 0}
                onChange={(newCell: number) => {
                  setSelectedCellId(newCell)
                }}
                options={cells?.map((cell: Cell) => ({
                  value: cell.id,
                  displayText: cell.displayName,
                  subText: `${cell.cellType?.toLowerCase()} cell`
                }))} />
            </div>
          </div>
          <div>
            <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">
              Username *
            </label>
            <div className="mt-2">
              <input
                type="text"
                name="username"
                id="username"
                value={username}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  normaliseAndSetUsername(e.target.value)
                }}
                className="block w-full pl-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
              Description
            </label>
            <div className="mt-2">
              <input
                type="text"
                name="description"
                id="description"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDescription(e.target.value)
                }}
                autoComplete="postal-code"
                className="block w-full pl-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <label htmlFor="additionalFilter" className="block text-sm font-medium leading-6 text-gray-900">
              Additional Filter
            </label>
            <div className="mt-2">
              <input
                type="text"
                name="additionalFilter"
                id="additionalFilter"
                value={additionalFilter}
                readOnly
                className="block w-full pl-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              />
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddFilterPanel(!showAddFilterPanel)}
                  className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  {showAddFilterPanel ? 'Close' : 'Add Filter'}
                </button>
                {showAddFilterPanel && (
                  <span className="text-xs text-gray-500">Select a store and a landing zone, then click Add</span>
                )}
              </div>
              {showAddFilterPanel && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Store selector (grouped by LZ with search) */}
                  <div className="rounded-md border border-gray-200 p-3">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Select store</div>
                    <input
                      type="text"
                      placeholder="Filter stores..."
                      className="mb-2 w-full pl-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                      value={storeSearch}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStoreSearch(e.target.value)}
                    />
                    <div className="max-h-72 overflow-auto pr-1 space-y-2">
                      {landingZones?.filter((lz: any) => lz?.cellId === selectedCellId)?.map((lz: any) => {
                        const storesInLz = (pointOfSales || [])
                          .filter((pos: any) => pos?.landingZoneId === lz?.id)
                          .filter((pos: any) => (pos?.posName || '').toLowerCase().includes(storeSearch.toLowerCase()) || String(pos?.id).includes(storeSearch));
                        if (storesInLz.length === 0) return null;
                        return (
                          <div key={`sel-${lz.id}`}>
                            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{lz.name}</div>
                            <div className="space-y-1">
                              {storesInLz.map((pos: any) => (
                                <button
                                  key={`sel-store-${pos.id}`}
                                  type="button"
                                  onClick={() => setSelectedFilterStoreId(pos.id)}
                                  className={`w-full text-left px-2 py-1 rounded border border-transparent hover:bg-gray-50 ${selectedFilterStoreId === pos.id ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {pos?.logo ? (
                                      <img src={pos.logo} alt={`${pos.posName} logo`} className="w-5 h-5 rounded object-cover border border-gray-200" />
                                    ) : (
                                      <div className="w-5 h-5 rounded bg-gray-100 border border-gray-200" />)
                                    }
                                    <span className="truncate text-sm text-gray-900">{pos.posName}</span>
                                    <span className="ml-auto text-xs text-gray-500">ID: {pos.id}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {/* Unassigned group */}
                      {pointOfSales?.some((pos: any) => !pos?.landingZoneId) && (
                        <div>
                          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">No Landing Zone</div>
                          <div className="space-y-1">
                            {pointOfSales
                              .filter((pos: any) => !pos?.landingZoneId)
                              .filter((pos: any) => (pos?.posName || '').toLowerCase().includes(storeSearch.toLowerCase()) || String(pos?.id).includes(storeSearch))
                              .map((pos: any) => (
                                <button
                                  key={`sel-store-${pos.id}`}
                                  type="button"
                                  onClick={() => setSelectedFilterStoreId(pos.id)}
                                  className={`w-full text-left px-2 py-1 rounded border border-transparent hover:bg-gray-50 ${selectedFilterStoreId === pos.id ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {pos?.logo ? (
                                      <img src={pos.logo} alt={`${pos.posName} logo`} className="w-5 h-5 rounded object-cover border border-gray-200" />
                                    ) : (
                                      <div className="w-5 h-5 rounded bg-gray-100 border border-gray-200" />)
                                    }
                                    <span className="truncate text-sm text-gray-900">{pos.posName}</span>
                                    <span className="ml-auto text-xs text-gray-500">ID: {pos.id}</span>
                                  </div>
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Landing Zone selector */}
                  <div className="rounded-md border border-gray-200 p-3">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Select landing zone</div>
                    <FilteredDropdown
                      label="Landing Zone"
                      value={(selectedFilterLzId as any) || ''}
                      onChange={(newVal: string | number) => setSelectedFilterLzId(newVal)}
                      options={landingZones?.filter((lz: any) => lz?.cellId === selectedCellId)?.map((lz: any) => ({
                        value: lz.id,
                        displayText: lz.name,
                      }))}
                    />
                    <div className="mt-3">
                      <button
                        type="button"
                        disabled={!selectedFilterStoreId || !selectedFilterLzId}
                        onClick={() => {
                          if (!selectedFilterStoreId || !selectedFilterLzId) return;
                          const newToken = `${selectedFilterStoreId}:${selectedFilterLzId}`;
                          const existing = (additionalFilter || '').split(',').map(s => s.trim()).filter(Boolean);
                          if (!existing.includes(newToken)) {
                            const next = existing.length ? `${additionalFilter ? additionalFilter + ',' : ''}${newToken}` : newToken;
                            setAdditionalFilter(next);
                          }
                          setSelectedFilterStoreId(null);
                          setSelectedFilterLzId(null);
                        }}
                        className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${(!selectedFilterStoreId || !selectedFilterLzId) ? 'bg-blue-200 text-slate-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white focus-visible:outline-blue-600'}`}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Current filters list */}
                  <div className="md:col-span-2">
                    <div className="rounded-md border border-gray-200 p-3 mt-1">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Current filters</div>
                      {filterPairs.length === 0 ? (
                        <div className="text-xs text-gray-500">No filters added yet</div>
                      ) : (
                        <ul className="space-y-2">
                          {filterPairs.map(({ token, store, storeId, lz }) => (
                            <li key={`pair-${token}`} className="flex items-center gap-3 justify-between">
                              <div className="flex items-center gap-3 min-w-0">
                                {store?.logo ? (
                                  <img src={store.logo} alt={`${store?.posName || 'Store'} logo`} className="w-5 h-5 rounded object-cover border border-gray-200 flex-shrink-0" />
                                ) : (
                                  <div className="w-5 h-5 rounded bg-gray-100 border border-gray-200 flex-shrink-0" />
                                )}
                                <div className="truncate text-sm text-gray-900">
                                  <span className="truncate">{store?.posName || 'Unknown store'}</span>
                                  <span className="ml-2 text-xs text-gray-500">ID: {storeId}</span>
                                  <span className="mx-2 text-gray-400">→</span>
                                  <span className="truncate">{lz?.name || 'Unknown LZ'}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFilterToken(token)}
                                className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="androidDeviceId" className="block text-sm font-medium leading-6 text-gray-900">
              Device Id
            </label>
            <div className="mt-2">
              <input
                type="text"
                name="androidDeviceId"
                id="androidDeviceId"
                value={androidDeviceId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAndroidDeviceId(e.target.value)
                }}
                className="block w-full pl-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

        </div>

        {/* Full-width checkbox grid grouped by Landing Zone */}
        <div className="sm:col-span-6">
          <div className="mt-2">
            <label className="block text-sm font-medium leading-6 text-gray-900">Select stores by landing zone</label>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6 items-stretch">
              {landingZones?.filter((lz: any) => lz?.cellId === selectedCellId)?.map((lz: any) => {
                const storesInLz = pointOfSales?.filter((pos: any) => pos?.landingZoneId === lz?.id) || [];
                const filter = (lzFilters?.[lz?.id] || '').toLowerCase();
                let filteredStores = filter
                  ? storesInLz.filter((pos: any) => (pos?.posName || '').toLowerCase().includes(filter))
                  : storesInLz;
                if (lzOnlySelected?.[lz?.id]) {
                  filteredStores = filteredStores.filter((pos: any) => pointOfSaleIds?.includes(pos.id));
                }
                return (
                  <div
                    key={lz?.id}
                    className="rounded-md border border-gray-200 p-3 flex flex-col h-96"
                  >
                    <div className="text-sm font-semibold text-gray-700 mb-2">{lz?.name}</div>
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="text"
                        placeholder="Filter stores..."
                        className="flex-1 pl-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        value={lzFilters?.[lz?.id] || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLzFilters({ ...(lzFilters || {}), [lz.id]: e.target.value })}
                      />
                      <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                          checked={!!lzOnlySelected?.[lz?.id]}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLzOnlySelected({ ...(lzOnlySelected || {}), [lz.id]: e.target.checked })}
                        />
                        Only selected
                      </label>
                    </div>
                    <div className="space-y-2 overflow-auto pr-1 flex-1">
                      {filteredStores.length > 0 ? filteredStores.map((pos: any) => {
                        const checked = pointOfSaleIds?.includes(pos.id);
                        return (
                          <label key={pos.id} className="flex items-center gap-3 text-sm text-gray-900">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                              checked={checked}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (e.target.checked) {
                                  setPointOfSaleIds([...(pointOfSaleIds || []), pos.id]);
                                } else {
                                  setPointOfSaleIds((pointOfSaleIds || []).filter((id: number) => id !== pos.id));
                                }
                              }}
                            />
                            {pos?.logo ? (
                              <img
                                src={pos.logo}
                                alt={`${pos.posName} logo`}
                                className="w-6 h-6 rounded object-cover border border-gray-200"
                              />
                            ) : (
                              <div className="w-6 h-6 rounded bg-gray-100 border border-gray-200" />
                            )}
                            <span className="truncate">{pos.posName}</span>
                          </label>
                        );
                      }) : (
                        <div className="text-xs text-gray-500">No stores</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Unassigned landing zone */}
              {pointOfSales?.some((pos: any) => !pos?.landingZoneId) && (
                <div
                  className="rounded-md border border-gray-200 p-3 flex flex-col h-96"
                >
                  <div className="text-sm font-semibold text-gray-700 mb-2">No Landing Zone</div>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="text"
                      placeholder="Filter stores..."
                      className="flex-1 pl-3 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                      value={lzFilters?.unassigned || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLzFilters({ ...(lzFilters || {}), unassigned: e.target.value })}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                        checked={!!lzOnlySelected?.unassigned}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLzOnlySelected({ ...(lzOnlySelected || {}), unassigned: e.target.checked })}
                      />
                      Only selected
                    </label>
                  </div>
                  <div className="space-y-2 overflow-auto pr-1 flex-1">
                    {pointOfSales
                      .filter((pos: any) => !pos?.landingZoneId)
                      .filter((pos: any) => (pos?.posName || '').toLowerCase().includes((lzFilters?.unassigned || '').toLowerCase()))
                      .filter((pos: any) => !lzOnlySelected?.unassigned || pointOfSaleIds?.includes(pos.id))
                      .map((pos: any) => {
                      const checked = pointOfSaleIds?.includes(pos.id);
                      return (
                        <label key={pos.id} className="flex items-center gap-3 text-sm text-gray-900">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                            checked={checked}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              if (e.target.checked) {
                                setPointOfSaleIds([...(pointOfSaleIds || []), pos.id]);
                              } else {
                                setPointOfSaleIds((pointOfSaleIds || []).filter((id: number) => id !== pos.id));
                              }
                            }}
                          />
                          {pos?.logo ? (
                            <img
                              src={pos.logo}
                              alt={`${pos.posName} logo`}
                              className="w-6 h-6 rounded object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded bg-gray-100 border border-gray-200" />
                          )}
                          <span className="truncate">{pos.posName}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {merchantDeviceCreationError ? <div className="sm:col-span-3">
          <div className="rounded-md bg-red-50 p-4 border border-1 border-red-200 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error creating a merchant device</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>Please try again or contact the development team</p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <Link to={`../merchant-devices`}
                      className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                    >
                      Go back
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> : <></>}

        {merchantDeviceUpdateError ? <div className="sm:col-span-3">
          <div className="rounded-md bg-red-50 p-4 border border-1 border-red-200 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Updating a merchant device</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>Please try again or contact the development team</p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <Link to={`../merchant-devices`}
                      className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                    >
                      Go back
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> : <></>}

        {merchantDeviceDeletionError ? <div className="sm:col-span-3">
          <div className="rounded-md bg-red-50 p-4 border border-1 border-red-200 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Deleting a merchant device</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>Please try again or contact the development team</p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <Link to={`../merchant-devices`}
                      className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                    >
                      Go back
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> : <></>}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  pointOfSales: state.admin.pointOfSales,
  isCreatingMerchantDevice: state.merchantDevices.isCreatingMerchantDevice,
  isUpdatingMerchantDevice: state.merchantDevices.isUpdatingMerchantDevice,
  isDeletingMerchantDevice: state.merchantDevices.isDeletingMerchantDevice,
  merchantDeviceCreationError: state.merchantDevices.merchantDeviceCreationError,
  merchantDeviceUpdateError: state.merchantDevices.merchantDeviceUpdateError,
  merchantDeviceDeletionError: state.merchantDevices.merchantDeviceDeletionError,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getCells: () => getCells()(dispatch),
  setSelectedCellId: (cellId: Cell['id']) => setSelectedCell(cellId)(dispatch),
  getPointofSales: (cellId: Cell['id']) => getPointOfSales(cellId)(dispatch),
  getLandingZonesInCell: (cellId: Cell['id']) => getLandingZonesInCell(cellId)(dispatch),
  createMerchantDeviceForCell: async (cellId: Cell['id'], merchantDevice: MerchantDeviceCreationData) => createMerchantDeviceForCell(cellId, merchantDevice)(dispatch),
  updateMerchantDevice: (cellId: Cell['id'], merchantDeviceId: string, merchantDevice: MerchantDevice) => updateMerchantDevice(cellId, merchantDeviceId, merchantDevice)(dispatch),
  deleteMerchantDeviceById: (cellId: Cell['id'], merchantDeviceId: string) => deleteMerchantDeviceById(cellId, merchantDeviceId)(dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConfigureMerchantDevice);
